import {Injectable, OnDestroy} from "@angular/core";
import {Subscription} from "rxjs";
import {MessageDetails, MessageLevel} from "../notification/message/message";
import {DataClientErrorMessage, EntityData} from "@fedelta-pos/data-client";
import {NotificationService} from "../notification/notification.service";
import {TranslateService} from "ng2-translate";
import {OrderingContext, OrderingContextIntention} from "../ordering-context/ordering-context";
import {IdentityModel} from "../identity/identity.model";
import {OrderModel} from "../order/order.model";
import {OrderingContextModel} from "../ordering-context/ordering-context.model";
import {OrchestrationEvents} from "../orchestration/orchestration-events";
import {DealEvents} from "./deal-events";
import {MenuEvents} from "../menu/menu-events";
import {LoyaltyCardData} from "../identity/member-summary-entity.model";
import {
    LoyaltyCardRedemptionRequestData,
    LoyaltyCardRedemptionService
} from "../loyalty-card/loyalty-card-redemption.service";
import {
    ActiveDealValidationRequestData,
    ActiveDealValidationService
} from "./active-deal-validation.service";
import {
    DealAvailability,
    DealData,
    DealEntityModel,
    DealProductData,
    DealResultCode,
    DealResult,
    PendingDealStatus
} from "./deal-entity.model";
import {SessionModel} from "../orchestration/session.model";
import {MenuModel} from "../menu/menu.model";
import {OrderType} from "../order/order";
import {NavigationService} from "../orchestration/navigation.service";

@Injectable()
export class DealModel implements OnDestroy {
    public pendingDeal: DealData;
    private _pendingDealStatus: PendingDealStatus = PendingDealStatus.NONE;
    public appliedDeals: DealData[] = [];

    // Avoid injection dependencies on OrderModel and OrderingContextController
    private _orderModel: OrderModel;

    private subscriptions: Subscription = new Subscription();

    constructor(private activeDealValidationService: ActiveDealValidationService,
                private loyaltyCardRedemptionService: LoyaltyCardRedemptionService,
                private dealEntityModel: DealEntityModel,
                private menuModel: MenuModel,
                private sessionModel: SessionModel,
                private orderingContextModel: OrderingContextModel,
                private identityModel: IdentityModel,
                private navigationService: NavigationService,
                private translate: TranslateService,
                private notificationService: NotificationService) {
        console.log("--- DealModel");

        this.subscriptions.add(OrchestrationEvents.sessionReset$.subscribe(() => this.removeAllDeals()));
    }

    set orderModel(orderModel: OrderModel) {
        this._orderModel = orderModel;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    // noinspection JSUnusedGlobalSymbols
    public get pendingDealStatus(): PendingDealStatus {
        return this._pendingDealStatus;
    }

    public set pendingDealStatus(status: PendingDealStatus) {
        if (this._pendingDealStatus !== status) {
            this._pendingDealStatus = status;
            this.notifyDealStatusChanged();
        }
    }

    public notifyDealStatusChanged() {
        DealEvents.dealStatusChanged$.next();
    }

    public getDealName(deal: DealData, useDefault: boolean = true) {
        return !deal ? undefined : (deal.title || deal.name || (useDefault ? this.translate.instant("woa.label.order.deal") : undefined));
    }

    public dealIsSuppressed(deal: DealData): boolean {
        return deal.suppressDealCardIfAnyOrdered ? this._orderModel.hasOrderedAnyItemPersonally
            : deal.suppressDealCardIfOrdered ? this.hasOrderedIncludedSellables(deal)
                : false;
    }

    public hasOrderedIncludedSellables(deal: DealData): boolean {
        return deal.products.some((product: DealProductData) => this._orderModel.hasOrderedSellablePersonally(product.sellable.sellableId));
    }

    public applyDeal(deal: DealData): void {
        if (this.appliedDeals.some((appliedDeal: DealData) => appliedDeal.id === deal.id)) {
            return;
        }

        this.appliedDeals.push(deal);
        this.pendingDeal = undefined;
        this._pendingDealStatus = PendingDealStatus.NONE;
        this.notificationService.displayFooterMessage(new MessageDetails(
            MessageLevel.SUCCESS, "woa.message.deals.applied", {"dealName": this.getDealName(deal)}, "tags"), 10);
        this.notifyDealStatusChanged();
    }

    public get dealLimit(): number {
        return this.sessionModel.currentApp.dealLimit;
    }

    public get dealLimitReached(): boolean {
        return this.sessionModel.currentApp.dealLimit && this.appliedDeals.length >= this.sessionModel.currentApp.dealLimit;
    }

    public findAppliedDealById(dealId: string): DealData {
        return this.appliedDeals.find((deal: DealData) => deal.id === dealId);
    }

    public cancelPendingDeal(): void {
        if (this.pendingDeal) {
            const dealId: string = this.pendingDeal.id;
            this.pendingDeal = undefined;
            this._pendingDealStatus = PendingDealStatus.NONE;

            // Remove the deal items from the order and save the order.
            // NB: This must be done before notifying the deal status change, or else we can end up saving
            // a new order during load of the ordering page, and thus preventing the current order from being restored.
            this._orderModel.removeDealItems([dealId]);
            this.notifyDealStatusChanged();
        }
    }

    public removeDeal(deal: DealData): void {
        this.removeDeals((appliedDeal: DealData) => appliedDeal === deal);
    }

    public removeAllDeals(): void {
        this.pendingDeal = undefined;
        this._pendingDealStatus = PendingDealStatus.NONE;
        this.removeDeals(() => true);
    }

    public removeDeals(predicate: (deal: DealData) => boolean): void {
        const dealIdsToRemove: string[] = [];
        this.appliedDeals = this.appliedDeals.filter((deal: DealData) => {
            if (predicate(deal)) {
                dealIdsToRemove.push(deal.id);
                return false;
            }
            return true;
        });
        if (dealIdsToRemove.length) {

            // Remove the deals from the order and save the order.
            // NB: This must be done before notifying the deal status change, or else we can end up saving
            // a new order during load of the ordering page, and thus preventing the current order from being restored.
            this._orderModel.removeDealItems(dealIdsToRemove);
            this.notifyDealStatusChanged();
        }
    }

    /**
     * Looks up an deal by the specified deal code, and starts ordering it.
     * @return DealStatus indicating if the deal is immediately ordered (IMMEDIATE), cannot be ordered (INVALID), or will be ordered after proceeding to the ordering page (DEFERRED)
     * @param dealCode
     */
    public async orderDealByDealCode(dealCode: string): Promise<DealResult> {
        const result: DealResult = await this.loadDealByDealCode(dealCode);
        return result.resultCode === DealResultCode.LOADED ? this.orderDeal(result.deal) : result;
    }

    /**
     * Starts ordering the specified deal.
     * @return DealResult indicating the result of attempting to order the deal
     * @param deal
     */
    public async orderDeal(deal: DealData): Promise<DealResult> {

        // If the deal availability has already been assessed notify the user immediately of any issues
        let unavailableReason: string = this.getDealUnavailableReason(deal, deal.availability);
        if (unavailableReason) {
            return new DealResult(DealResultCode.UNAVAILABLE, unavailableReason);
        }

        // Check that the deal has not already been ordered
        if (this.findAppliedDealById(deal.id)) {
            return new DealResult(DealResultCode.UNAVAILABLE, "woa.heading.order.dealAlreadyOnOrder");
        }

        // If the deal limit is 1, and there is a current deal, confirm that the user wants to replace it.
        if (this.sessionModel.currentApp.dealLimit == 1 && this.appliedDeals.length >= 1) {

            // Perform age check if necessary for the selected deal before prompting for replacement
            if (this.shouldApplyMinimumAgeToDeal(deal) && !await this.identityModel.verifyAge()) {
                return new DealResult(DealResultCode.DECLINED);
            }

            if (!await this.notificationService.displayConfirmation("woa.heading.order.confirmReplaceDeal", undefined,
                "woa.message.order.confirmReplaceSingleDeal",
                "woa.button.common.noThanks", "woa.button.deals.orderDeal", false)) {
                return new DealResult(DealResultCode.DECLINED);
            }

            this.removeAllDeals();
        }

        // Only one deal with a direct discount is allowed per order.
        // If there is an incompatible deal already applied, confirm that the user wants to replace it.
        if (!!deal.discount.discountId) {
            const incompatibleDeal: DealData = this.appliedDeals.find((appliedDeal: DealData) => !!appliedDeal.discount.discountId);
            if (incompatibleDeal) {

                // Perform age check if necessary for the selected deal before prompting for replacement
                if (this.shouldApplyMinimumAgeToDeal(deal) && !await this.identityModel.verifyAge()) {
                    return new DealResult(DealResultCode.DECLINED);
                }

                if (!await this.notificationService.displayConfirmation("woa.heading.order.confirmReplaceDeal", undefined,
                    "woa.message.order.confirmReplaceIncompatibleDeal",
                    "woa.button.common.noThanks", "woa.button.deals.orderDeal", false,
                    {incompatibleDealName: this.getDealName(incompatibleDeal)})) {
                    return new DealResult(DealResultCode.DECLINED);
                }

                this.removeDeal(incompatibleDeal);
            }
        }

        // If the deal limit has been reached, then other than for the limit = 1 case above,
        // we require the user to manually remove one or more deals and try again.
        if (this.sessionModel.currentApp.dealLimit && this.appliedDeals.length >= this.sessionModel.currentApp.dealLimit) {
            if (await this.notificationService.displayConfirmation("woa.heading.order.dealLimitReached", undefined,
                "woa.message.order.dealLimitReached",
                "woa.button.common.cancel", "woa.button.deals.showActiveDeals", false,
                {limit: this.sessionModel.currentApp.dealLimit})) {
                // noinspection ES6MissingAwait
                this.navigationService.createPage("woa-deal-code-entry-page");
            }
            // noinspection ES6MissingAwait
            return new DealResult(DealResultCode.DECLINED);
        }

        // We force store and order type selection before applying a deal.
        // This method will be called again when and if the ordering context is applied.
        // Note that the deal availability is cleared as it will have to be reassessed once the selection has been made.
        if (!this.orderingContextModel.currentStore || !this.orderingContextModel.orderType) {
            deal.availability = undefined;
            await this.orderingContextModel.selectAndApplyStoreAndOrderType(new OrderingContext()
                .withIntention(OrderingContextIntention.ORDER)
                .withSelectedTable(this.orderingContextModel.preselectedTableSelection));
            if (!this.orderingContextModel.currentStore || !this.orderingContextModel.orderType) {
                return new DealResult(DealResultCode.DECLINED);
            }
        }

        // If the deal availability is not already known (not selected from a deal code, or if a store or order type selection has been forced above),
        // we need to assess/reassess whether the deal is valid for the currently selected store and order type.
        if (!deal.availability) {
            try {
                const availability: DealAvailability = await this.getDealAvailability(deal);
                let unavailableReason: string = this.getDealUnavailableReason(deal, availability);
                if (unavailableReason) {
                    return new DealResult(DealResultCode.UNAVAILABLE, unavailableReason);
                }
            } catch (error) {
                await this.notificationService.displayAlert(null, "woa.message.order.dealLookupFailed");
                console.log(error);
                throw error;
            }
        }

        // Perform age check if necessary for the selected deal before applying
        if (this.shouldApplyMinimumAgeToDeal(deal) && !await this.identityModel.verifyAge()) {
            return new DealResult(DealResultCode.DECLINED);
        }

        // Apply the deal
        if (deal.products.length === 0) {
            this.applyDeal(deal);
            return new DealResult(DealResultCode.IMMEDIATE);
        } else {
            this.pendingDeal = deal;
            this.pendingDealStatus = PendingDealStatus.PRODUCT_ORDER_REQUIRED;
            return new DealResult(DealResultCode.DEFERRED);
        }
    }

    public shouldApplyMinimumAgeToDeal(deal: DealData): boolean {
        return this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.minimumAge && MenuEvents.currentMenu.value &&
            deal.products.some((dealProduct: DealProductData) => this.menuModel.minimumAgeAppliesToSellable(dealProduct.sellable.sellableId));
    }

    private async loadDealByDealCode(dealCode: string): Promise<DealResult> {
        try {
            await this.dealEntityModel.loadEntity(dealCode, {localDateTime: EntityData.formatLocalDateTime(this.sessionModel.now)});
            return new DealResult(DealResultCode.LOADED, undefined, this.dealEntityModel.data);
        } catch (error) {
            if (error instanceof DataClientErrorMessage && error.status === 404) {
                return new DealResult(DealResultCode.UNAVAILABLE, "woa.message.order.dealNotValid");
            }
            await this.notificationService.displayAlert(null, "woa.message.order.dealLookupFailed");
            console.log(error);
            throw error;
        }
    }

    private async getDealAvailability(deal: DealData): Promise<DealAvailability> {
        const activeDealValidationRequest: ActiveDealValidationRequestData = <ActiveDealValidationRequestData>EntityData.create(ActiveDealValidationRequestData);
        activeDealValidationRequest.dealId = deal.id;
        activeDealValidationRequest.integratedDealConfigId = deal.integratedDealConfigId;
        activeDealValidationRequest.providerName = deal.providerName;
        activeDealValidationRequest.localDateTime = EntityData.formatLocalDateTime(this.sessionModel.now);
        const result: any = await this.activeDealValidationService.performAction(activeDealValidationRequest);
        return result["availability"];
    }

    /**
     * Returns a message for display to the user (if necessary) for the specified deal availability.
     * For unavailability due to current store or order type, no notification is required if a store and order type are not yet selected.
     * This is because store and order type selection will be required, after which the availability will be reassessed.
     *
     * @private
     * @param data
     * @param availability
     */
    private getDealUnavailableReason(data: DealData, availability: DealAvailability): string {
        switch (availability) {
            case DealAvailability.INVALID:
                return "woa.message.order.dealNotValid";
            case DealAvailability.EXPIRED:
                return "woa.message.order.dealExpired";
            case DealAvailability.CONSUMED:
                return "woa.message.order.dealConsumed";
            case DealAvailability.DISCONTINUED:
                return "woa.message.order.dealDiscontinued";
            case DealAvailability.UNAVAILABLE_AT_STORE:
                if (data.providerName || (this.orderingContextModel.currentStore && this.orderingContextModel.orderType)) {
                    return "woa.message.order.dealNotAvailableAtStore";
                }
                return undefined;
            case DealAvailability.UNSCHEDULED_AT_STORE:
                if (data.providerName || (this.orderingContextModel.currentStore && this.orderingContextModel.orderType)) {
                    return "woa.message.order.dealNotScheduledAtStore";
                }
                return undefined;
            case DealAvailability.BEFORE_VALID_PERIOD:
                return "woa.message.order.dealBeforeValidPeriod";
            case DealAvailability.OUTSIDE_WEEKDAY_TIME_RESTRICTION:
                return "woa.message.order.dealOutsideWeekdayTimeRestriction";
            default:
                if (!data.isAvailableForOrderType(this.orderingContextModel.orderType)) {
                    switch (this.orderingContextModel.orderType) {
                        case OrderType.ORDER_AHEAD:
                            return "woa.message.order.dealNotAvailableOrderAhead";
                        case OrderType.ORDER_DELIVERY:
                            return "woa.message.order.dealNotAvailableOrderDelivery";
                        case OrderType.ORDER_AT_TABLE:
                            return "woa.message.order.dealNotAvailableOrderAtTable";
                    }
                }
                return undefined;
        }
    }

    public async redeemLoyaltyCard(loyaltyCard: LoyaltyCardData): Promise<void> {
        const loyaltyCardRedemptionRequest: LoyaltyCardRedemptionRequestData = <LoyaltyCardRedemptionRequestData>EntityData.create(LoyaltyCardRedemptionRequestData);
        loyaltyCardRedemptionRequest.loyaltyCardId = loyaltyCard.id;
        loyaltyCardRedemptionRequest.integratedLoyaltyConfigId = loyaltyCard.integratedLoyaltyConfigId;
        loyaltyCardRedemptionRequest.providerName = loyaltyCard.providerName;
        try {
            return await this.loyaltyCardRedemptionService.performAction(loyaltyCardRedemptionRequest);
        } catch (error) {
            if (error.displayable) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, error.data.messageKey, error.data.messageParams));
            } else {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.message.order.loyaltyCardRedemptionFailed"));
            }
            return undefined;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/deal/deal.model.ts